import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import TextInput from "components/inputs/textInput"
import useProducts from "hooks/useProducts"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import { decodeFirebaseError } from "utils/firebaseHelpers"

interface DeleteCategoryFormProps {
    storageId: string,
    productId: string
}

const DeleteProductForm: React.FC<DeleteCategoryFormProps> = ({ storageId, productId }) => {

    const { t } = useTranslation()

    // Form productsStatus.
    const [errors, setErrors] = useState<string[]>([]);
    const [confirmation, setConfirmation] = useState(``)
    const [confirmed, setConfirmed] = useState(false)
    const [name, setName] = useState(``)

    // Redux hooks.
    const navigate = useNavigate()

    const {
        product,
        productsStatus,
        deleteProductAsync,
        isDeleting
    } = useProducts({ storageId, productId })

    const { closeDialog, addToast } = useLayout()

    // Catch product name from storage.
    useEffect(() => {
        if (!product) return
        setName(product.name)
    }, [product])

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setErrors([])
        if (!confirmed) {
            setErrors([t('errorConfirmAction')])
            return
        }
        try {
            await deleteProductAsync({ storageId, productId })
            addToast({ text: t('toastProductDeleted'), colorScheme: 'green' })
            closeDialog()
            navigate(`/storage/products`)
        } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e instanceof Error ? e.message : String(e))
            setErrors([errorMessage])
        }
    }

    // Handle confirmation change.
    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === name)
    }

    // Handle certain states.
    if (!storageId) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>
    if (!productId) return <Callout type={'error'}>{t('productId')}</Callout>
    if (productsStatus === `loading`) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingStorage')}</Callout>

    // Print the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isDeleting}
        >
            <Section>
                <p>{t('deleteProductConfirmation', { name })}</p>
                <TextInput label={t('typeProductNameToConfirm')} value={confirmation} onChange={handleConfirmationChange} required placeholder={product?.name} />
                <ButtonGroup>
                    <Button wideOnMobile type="button" design="optional" onClick={closeDialog}><CloseIcon /><span>{t('buttonCancel')}</span></Button>
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger"><DeleteIcon /><span>{t('delete')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default DeleteProductForm



